import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { MenuIcon, CartIcon, SearchIcon, ChevronLeftIcon, CloseIcon } from './common/Icons';
import { Settings, Globe, Moon, Sun, ChevronRight } from 'lucide-react';
import {
  Diamond,
  Drop,
  Gift,
  Lightning,
  MagnifyingGlass,
  Moon as MoonThin,
  Star,
  Sun as SunThin,
  UserCircle,
  Wallet,
} from '@phosphor-icons/react';
import CrossedSwords from './icons/CrossedSwords';
import XmarketLogo from './icons/XmarketLogo';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageCurrencyModal from './LanguageCurrencyModal';
import UserMenu from './UserMenu';
import MobileTabBar from './MobileTabBar';
import { getCachedUser, isAuthenticated } from '../utils/auth';
import { getUnreadMessageCount } from '../utils/messageCount';
import { useCart } from '../contexts/CartContext';
import { getEffectivePrice } from '../utils/formatPrice';
import { preloadAllIcons } from '../utils/iconPreloader';
import { ALL_GAME_NAMES as allGames, getGameSlug } from '../config/allGames';
import { isMarketplaceChromePath } from '../config/gameServiceCategories';
import { getGameIcon } from '../utils/getGameIcon';
import '../styles/Header.css';
import '../styles/HomePage.css';
import { useTranslation } from '../hooks/useTranslation';
import { GAME_SEARCH_ALIASES as gameAliases } from '../config/gameSearchAliases';
import {
  NAVBAR_SEARCH_POPULAR_ORDER,
  interleaveNavbarCategoryRows,
  matchesGame,
  rankedSearchGames,
  sortGamesBySearchRelevance,
  sortNavbarCategoriesForSearch,
} from '../utils/gameCatalogSearch';

const HOME_HEADER_ICONS = {
  orders: '/main/header/orders.svg',
  chat: '/main/header/chat.svg',
  wallet: '/main/header/wallet.svg',
};

function homeHeaderIconStyle(src) {
  return {
    WebkitMaskImage: `url(${src})`,
    maskImage: `url(${src})`,
  };
}

function HomeHeaderAvatar() {
  const user = getCachedUser();
  if (!user) {
    return (
      <span className="header-home-avatar header-home-avatar--empty" aria-hidden>
        <UserCircle size={18} weight="fill" />
      </span>
    );
  }

  const cachedAvatar = user.id ? localStorage.getItem(`avatar_${user.id}`) : null;
  if (cachedAvatar) {
    return <img src={cachedAvatar} alt="" className="header-home-avatar" />;
  }

  if (user.avatar_url && !user.avatar_url.startsWith('letter:')) {
    return <img src={user.avatar_url} alt="" className="header-home-avatar" />;
  }

  const letter = user.avatar_url?.startsWith('letter:')
    ? user.avatar_url.split(':')[1]
    : (user.username || 'U').charAt(0).toUpperCase();

  return <span className="header-home-avatar header-home-avatar--letter">{letter}</span>;
}

function HomeHeaderTools({ userLoggedIn, location }) {
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!userLoggedIn) {
      setUnreadCount(0);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      const count = await getUnreadMessageCount();
      if (!cancelled) setUnreadCount(count);
    };

    load();
    const interval = window.setInterval(load, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [userLoggedIn]);

  const to = (path) => (userLoggedIn ? path : '/auth?signin=true');
  const state = userLoggedIn ? undefined : { backgroundLocation: location };

  return (
    <>
      <Link to={to('/profile/orders/purchased')} state={state} className="header-home-tool">
        <span className="header-home-tool-glyph" style={homeHeaderIconStyle(HOME_HEADER_ICONS.orders)} aria-hidden />
        <span>Orders</span>
      </Link>
      <Link to={to('/messages')} state={state} className="header-home-tool">
        <span className="header-home-tool-iconwrap">
          <span className="header-home-tool-glyph" style={homeHeaderIconStyle(HOME_HEADER_ICONS.chat)} aria-hidden />
          {unreadCount > 0 ? (
            <span className="header-home-tool-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
          ) : null}
        </span>
        <span>Chats</span>
      </Link>
      <Link to={to('/profile/wallet')} state={state} className="header-home-tool">
        <span className="header-home-tool-glyph" style={homeHeaderIconStyle(HOME_HEADER_ICONS.wallet)} aria-hidden />
        <span>Wallet</span>
      </Link>
      <Link to={to('/profile')} state={state} className="header-home-tool">
        <span className="header-home-tool-iconwrap">
          <HomeHeaderAvatar />
        </span>
        <span>Profile</span>
      </Link>
    </>
  );
}

function CategoryGamesPanel({
  title,
  hideTitle = false,
  searchQuery,
  onSearchChange,
  items,
  onNavigate,
}) {
  return (
    <div className="xm-cat-panel">
      <div className="xm-cat-panel-toolbar">
        {hideTitle ? (
          <span className="xm-cat-panel-count">{items.length} games</span>
        ) : (
          <div className="xm-cat-panel-heading">
            <p className="xm-cat-panel-kicker">Browse</p>
            <h3>{title}</h3>
            <span className="xm-cat-panel-count">{items.length}</span>
          </div>
        )}
        <label className="xm-cat-panel-search">
          <MagnifyingGlass size={16} weight="regular" />
          <input
            type="search"
            placeholder="Search games"
            value={searchQuery}
            onChange={(event) => onSearchChange(event.target.value)}
            autoComplete="off"
            spellCheck="false"
          />
        </label>
      </div>
      <div className="xm-cat-panel-list">
        {items.length === 0 ? (
          <p className="xm-cat-panel-empty">No games match that search.</p>
        ) : (
          <div className="xm-cat-panel-grid">
            {items.map((item) => (
              <Link key={item.game} to={item.href} className="xm-cat-game" onClick={onNavigate}>
                {item.iconPath ? (
                  <img
                    src={item.iconPath}
                    alt=""
                    className="xm-cat-game-icon"
                    loading="lazy"
                    decoding="async"
                    onError={(event) => {
                      const src = event.target?.src;
                      if (src && src.endsWith('.avif')) {
                        event.target.src = src.replace(/\.avif$/i, '.webp');
                      }
                    }}
                  />
                ) : (
                  <span className="xm-cat-game-icon xm-cat-game-icon--fallback" aria-hidden>
                    {item.game.charAt(0)}
                  </span>
                )}
                <span className="xm-cat-game-name">{item.game}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Header = () => {
  const location = useLocation();
  const isHome = isMarketplaceChromePath(location.pathname);
  const isProfile = location.pathname.startsWith('/profile');
  const { t } = useTranslation();
  const { getCartCount, isCartDrawerOpen, setCartDrawerOpen, cartItems, getCartTotal, removeFromCart } = useCart();
  const cartCount = getCartCount();
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [navbarSearchQuery, setNavbarSearchQuery] = useState('');
  const [showNavbarSearchResults, setShowNavbarSearchResults] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileSearchInputRef = useRef(null);
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      const saved = localStorage.getItem('recentSearches');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  const closeMobileSearch = useCallback(() => {
    setMobileSearchOpen(false);
    setNavbarSearchQuery('');
    mobileSearchInputRef.current?.blur();
  }, []);

  // Track window size for desktop detection
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth > 768;
      setIsDesktop(desktop);
      if (desktop) setMobileSearchOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    closeMobileSearch();
  }, [location.pathname, closeMobileSearch]);

  // Prevent body scroll when search is open (desktop only) or mobile search sheet is open
  useEffect(() => {
    if ((showNavbarSearchResults && isDesktop) || mobileSearchOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      preloadAllIcons();
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && mobileSearchOpen) closeMobileSearch();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [showNavbarSearchResults, isDesktop, mobileSearchOpen, closeMobileSearch]);

  // Cart drawer: lock body scroll and Escape to close
  useEffect(() => {
    if (!isCartDrawerOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setCartDrawerOpen(false);
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isCartDrawerOpen]);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userLoggedIn, setUserLoggedIn] = useState(() => {
    // Initialize with actual auth state to prevent flash
    try {
      const token = localStorage.getItem('auth_token');
      const expiresAt = localStorage.getItem('expires_at');
      const userId = localStorage.getItem('user_id');
      
      // Need all three to be considered logged in
      if (!token || !expiresAt || !userId) {
        return false;
      }
      
      // Check if token is expired
      const now = Math.floor(Date.now() / 1000);
      const expires = parseInt(expiresAt);
      
      if (isNaN(expires) || expires <= now) {
        // Clear expired auth data
        localStorage.removeItem('auth_token');
        localStorage.removeItem('expires_at');
        localStorage.removeItem('user_id');
        return false;
      }
      
      return true;
    } catch {
      return false;
    }
  });
  
  // Listen for login events to update immediately
  useEffect(() => {
    const handleLogin = () => {
      setUserLoggedIn(true);
    };
    
    window.addEventListener('user-login', handleLogin);
    
    return () => {
      window.removeEventListener('user-login', handleLogin);
    };
  }, []);
  const timeoutRef = useRef(null);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showLanguageCurrencyModal, setShowLanguageCurrencyModal] = useState(false);
  const [currency, setCurrency] = useState(() => {
    const savedCurrency = localStorage.getItem('currency');
    return savedCurrency || 'NOK';
  });
  const settingsDropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const isSearchInteractingRef = useRef(false);
  const { theme, toggleTheme } = useTheme();
  const { currentLanguage, changeLanguage, currentLanguageData, supportedLanguages } = useLanguage();

  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  ];

  const currentCurrency = currencies.find(curr => curr.code === currency) || currencies[3];

  // Close settings dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target)) {
        setShowSettingsDropdown(false);
      }
    };

    if (showSettingsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSettingsDropdown]);

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
  };

  const handleCurrencyChange = (currCode) => {
    setCurrency(currCode);
    localStorage.setItem('currency', currCode);
  };

  useEffect(() => {
    // Check authentication status - be more strict
    const checkAuth = () => {
      try {
        const token = localStorage.getItem('auth_token');
        const expiresAt = localStorage.getItem('expires_at');
        const userId = localStorage.getItem('user_id');
        
        // Need all three to be considered logged in
        if (!token || !expiresAt || !userId) {
          setUserLoggedIn(false);
          return;
        }
        
        // Check if token is expired
        const now = Math.floor(Date.now() / 1000);
        const expires = parseInt(expiresAt);
        
        if (isNaN(expires) || expires <= now) {
          // Clear expired auth data
          localStorage.removeItem('auth_token');
          localStorage.removeItem('expires_at');
          localStorage.removeItem('user_id');
          localStorage.removeItem('user_email');
          localStorage.removeItem('user_username');
          localStorage.removeItem('user_avatar_url');
          setUserLoggedIn(false);
          return;
        }
        
        // Only set to true if we have valid token and it's not expired
        setUserLoggedIn(true);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Auth check error:', error);
        }
        setUserLoggedIn(false);
      }
    };
    
    // Initial check
    checkAuth();
    
    // Listen for auth changes (but debounce to prevent rapid updates)
    let timeoutId;
    const debouncedCheckAuth = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkAuth, 50);
    };
    
    window.addEventListener('storage', debouncedCheckAuth);
    window.addEventListener('focus', checkAuth);
    
    // Listen for login event to update immediately (no delay)
    const handleUserLogin = () => {
      // Check immediately - localStorage should already be written
      checkAuth();
      // Also check again after a tiny delay to catch any race conditions
      setTimeout(checkAuth, 10);
    };
    window.addEventListener('user-login', handleUserLogin);
    
    // Also listen for custom logout event to update immediately
    const handleLogoutEvent = () => {
      setUserLoggedIn(false);
    };
    window.addEventListener('user-logout', handleLogoutEvent);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('storage', debouncedCheckAuth);
      window.removeEventListener('focus', checkAuth);
      window.removeEventListener('user-logout', handleLogoutEvent);
    };
  }, []);

  // Currency name mapping for display - shared with getGameCategories
  const currencyNameMapForDisplay = {
    '8-ball-pool': 'Coins',
    'albion-online': 'Silver',
    'animal-crossing-new-horizons': 'Bells',
    'arc-raiders': 'Coins',
    'black-desert-online': 'Silver',
    'blade-soul-neo': 'Divine Gems',
    'blade-ball': 'Tokens',
    'creatures-of-sonaria': 'Mushrooms',
    'dark-and-darker': 'Gold',
    'dc-universe-online': 'Cash',
    'delta-force': 'Tekniq Alloy',
    'diablo-4': 'Gold',
    'digimon-super-rumble': 'Bits',
    'dofus': 'Kamas',
    'donutsmp': 'Money',
    'dune-awakening': 'Solaris',
    'ea-sports-fc': 'Coins',
    'ea-sports-fc-mobile': 'Coins',
    'elden-ring': 'Runes',
    'elder-scrolls-online': 'Gold',
    'escape-from-tarkov': 'Roubles',
    'eve-echoes': 'ISK',
    'eve-online': 'ISK',
    'fallout-76': 'Caps',
    'final-fantasy-xi': 'Gil',
    'final-fantasy-xiv': 'Gil',
    'fisch': 'Money',
    'forza-horizon-5': 'Credits',
    'grow-a-garden': 'Tokens',
    'growtopia': 'Locks',
    'guild-wars-2': 'Gold',
    'hypixel': 'Coins',
    'last-epoch': 'Gold',
    'lost-ark': 'Gold',
    'madden-nfl': 'Coins',
    'maplestory': 'Mesos',
    'minecraft': 'Hypixel Coins',
    'mortal-online-2': 'Gold',
    'new-world': 'Coins',
    'odin-valhalla-rising': 'Diamonds',
    'old-school-runescape': 'Gold',
    'path-of-exile': 'Currency',
    'path-of-exile-2': 'Currency',
    'pet-simulator-99': 'Gems',
    'pets-go': 'Diamonds',
    'pokemon-go': 'Stardust',
    'roblox': 'Robux',
    'runescape-3': 'Gold',
    'runescape-bonds': 'Bonds',
    'star-citizen': 'aUEC',
    'swtor': 'Credits',
    'temtem': 'Pansun',
    'throne-and-liberty': 'Lucent',
    'toilet-tower-defense': 'Gems',
    'torchlight-infinite': 'Flame Elementium',
    'trove': 'Flux',
    'warframe': 'Platinum',
    'warcraft': 'Gold',
    'wow-classic': 'Era Gold', // Special case: "WoW Classic Era Gold"
    'wow-mists-of-pandaria': 'Gold'
  };

  // Top-ups name mapping for display
  const topUpsNameMapForDisplay = {
    '99-nights-in-the-forest': 'Diamonds',
    'amazon': 'Subscription',
    'apex-legends': 'Top Ups',
    'arena-breakout': 'Top Ups',
    'bigo-live': 'Diamonds',
    'blood-strike': 'Gold',
    'brawl-stars': 'Gems',
    'call-of-duty-mobile': 'CP',
    'cod-call-of-duty': 'Points',
    'call-of-duty': 'Points',
    'clash-of-clans': 'Gems',
    'clash-royale': 'Gems',
    'crunchyroll': 'Subscription',
    'dead-by-daylight': 'Auric Cells',
    'delta-force': 'Delta Coins',
    'destiny-2': 'Silver',
    'ea-play': 'Subscription',
    'ea-sports-fc': 'Points',
    'efootball': 'Coins',
    'elder-scrolls-online': 'Crowns',
    'etheria-restart': 'Crystals',
    'fortnite': 'V-Bucks',
    'garena-free-fire': 'Diamonds',
    'genshin-impact': 'Top Ups',
    'growtopia': 'Gems',
    'honkai-star-rail': 'Oneiric Shards',
    'honor-of-kings': 'Tokens',
    'identity-v': 'Echoes',
    'league-of-legends': 'Riot Points',
    'league-of-legends-wild-rift': 'Wild Cores',
    'love-and-deepspace': 'Crystals',
    'marvel-rivals': 'Lattice',
    'miliastra-wonderland': 'Chronal Nexus',
    'minecraft': 'Minecoins',
    'mobile-legends': 'Diamonds',
    'nintendo': 'Subscription',
    'overwatch-2': 'Coins',
    'path-of-exile-2': 'Points',
    'playstation': 'Subscription',
    'pokemon-go': 'Top Ups',
    'pokemon-tcg-pocket': 'Poke Gold',
    'poppo-live': 'Coins',
    'pubg': 'G-Coins',
    'pubg-mobile': 'UC',
    'rainbow-six-siege-x': 'Credits',
    'roblox': 'Top Ups',
    'rocket-league': 'Credits',
    'rust': 'Coins',
    'sea-of-thieves': 'Ancient Coins',
    'spotify': 'Subscription',
    'super-sus': 'Goldstars',
    'tiktok': 'Coins',
    'valorant': 'Points',
    'whiteout-survival': 'Frost Stars',
    'world-of-warcraft': 'Subscription',
    'warcraft': 'Subscription',
    'wuthering-waves': 'Lunites',
    'youtube': 'Subscription',
    'zenless-zone-zero': 'Monochromes'
  };

  // Games with top-ups pages
  const gamesWithTopUpsSlugs = Object.keys(topUpsNameMapForDisplay);

  // Currency name mapping for display in popular games
  const getCurrencyDisplayName = (gameName) => {
    const gameSlug = getGameSlug(gameName);
    // Special case: Hypixel should display as "Minecraft Hypixel Coins"
    if (gameSlug === 'hypixel') {
      return `Minecraft Hypixel ${currencyNameMapForDisplay[gameSlug]}`;
    }
    const currencyName = currencyNameMapForDisplay[gameSlug];
    return currencyName ? `${gameName} ${currencyName}` : gameName;
  };

  // Top-ups name mapping for display in popular games
  const getTopUpsDisplayName = (gameName) => {
    const gameSlug = getGameSlug(gameName);
    const topUpsName = topUpsNameMapForDisplay[gameSlug];
    return topUpsName ? `${gameName} ${topUpsName}` : gameName;
  };

  // Items name mapping for display
  const itemsNameMapForDisplay = {
    'adopt-me': 'Items',
    'albion-online': 'Items',
    'all-star-tower-defense-x': 'Items',
    'animal-crossing-new-horizons': 'Items',
    'anime-defenders': 'Items',
    'anime-eternal': 'Items',
    'anime-fighting-simulator-endless': 'Items',
    'anime-last-stand': 'Items',
    'anime-reborn': 'Items',
    'anime-vanguards': 'Items',
    'arc-raiders': 'Items',
    'ark-survival-ascended': 'Items',
    'attack-on-titan-revolution': 'Items',
    'battlefield': 'Items',
    'bee-swarm-simulator': 'Items',
    'blade-ball': 'Items',
    'blox-fruits': 'Items',
    'blue-lock-rivals': 'Items',
    'borderlands-4': 'Items',
    'bubble-gum-simulator-infinity': 'Items',
    'build-a-zoo': 'Items',
    'cod-call-of-duty': 'Items',
    'call-of-duty': 'Items',
    'clash-of-clans': 'Items',
    'counter-strike-2': 'Items',
    'cs2': 'Items',
    'creatures-of-sonaria': 'Items',
    'dark-and-darker': 'Items',
    'dead-rails': 'Items',
    'delta-force': 'Items',
    'destiny-2': 'Items',
    'diablo-2-resurrected': 'Items',
    'diablo-4': 'Items',
    'discord': 'Items',
    'donutsmp': 'Items',
    'dota-2': 'Items',
    'dune-awakening': 'Items',
    'ea-sports-fc': 'Items',
    'elden-ring': 'Items',
    'elder-scrolls-online': 'Items',
    'escape-from-tarkov': 'Items',
    'eve-online': 'Items',
    'fallout-76': 'Items',
    'final-fantasy-xiv': 'Items',
    'fisch': 'Items',
    'fish-it': 'Items',
    'fortnite': 'Items',
    'garden-tower-defense': 'Items',
    'granblue-fantasy': 'Items',
    'grand-piece-online': 'Items',
    'grow-a-garden': 'Items',
    'growtopia': 'Items',
    'guild-wars-2': 'Items',
    'hay-day': 'Items',
    'hunty-zombie': 'Items',
    'ink-game': 'Items',
    'jailbreak': 'Items',
    'knight-online': 'Items',
    'league-of-legends': 'Items',
    'lost-ark': 'Items',
    'hypixel': 'Items', // Minecraft Hypixel Items
    'mobile-legends': 'Items',
    'monopoly-go': 'Items',
    'murder-mystery-2': 'Items',
    'murderers-vs-sheriffs-duels': 'Items',
    'old-school-runescape': 'Items',
    'path-of-exile': 'Items',
    'path-of-exile-2': 'Items',
    'pet-simulator-99': 'Items',
    'pets-go': 'Items',
    'plants-vs-brainrots': 'Items',
    'pokemon-go': 'Items',
    'pokemon-legends-z-a': 'Items',
    'pokemon-tcg-pocket': 'Items',
    'raise-animals': 'Items',
    'roblox': 'Items',
    'roblox-limiteds': 'Items',
    'roblox-rivals': 'Items',
    'runescape-3': 'Items',
    'rust': 'Items',
    'steal-a-brainrot': 'Items',
    'team-fortress-2': 'Items',
    'the-forge': 'Items',
    'tips-jar': 'Items',
    'twitch-drops': 'Items',
    'universal-tower-defense': 'Items',
    'volleyball-legends': 'Items',
    'warframe': 'Items',
    'world-of-warcraft': 'Items',
    'warcraft': 'Items',
    'wow-classic': 'Items',
    'wow-mists-of-pandaria': 'Items'
  };

  // Games with items pages
  const gamesWithItemsSlugs = Object.keys(itemsNameMapForDisplay);

  // Items name mapping for display in popular games
  const getItemsDisplayName = (gameName) => {
    const gameSlug = getGameSlug(gameName);
    // Special case: Hypixel should display as "Minecraft Hypixel Items"
    if (gameSlug === 'hypixel') {
      return `Minecraft Hypixel ${itemsNameMapForDisplay[gameSlug]}`;
    }
    const itemsName = itemsNameMapForDisplay[gameSlug];
    return itemsName ? `${gameName} ${itemsName}` : gameName;
  };

  // Boosting name mapping for display
  const boostingNameMapForDisplay = {
    'anime-vanguards': 'Boosting',
    'apex-legends': 'Boosting',
    'arc-raiders': 'Boosting',
    'arena-breakout': 'Boosting',
    'arena-breakout-infinite': 'Boosting',
    'battlefield': 'Boosting',
    'black-desert-online': 'Boosting',
    'blox-fruits': 'Boosting',
    'brawl-stars': 'Boosting',
    'cod-call-of-duty': 'Boosting',
    'call-of-duty': 'Boosting',
    'clash-of-clans': 'Boosting',
    'clash-royale': 'Boosting',
    'counter-strike-2': 'Boosting',
    'cs2': 'Boosting',
    'dead-by-daylight': 'Boosting',
    'deadlock': 'Boosting',
    'delta-force': 'Boosting',
    'destiny-2': 'Boosting',
    'diablo-4': 'Boosting',
    'donutsmp': 'Boosting',
    'dota-2': 'Boosting',
    'ea-sports-fc': 'Boosting',
    'elder-scrolls-online': 'Boosting',
    'escape-from-tarkov': 'Boosting',
    'final-fantasy-xiv': 'Boosting',
    'fisch': 'Boosting',
    'fortnite': 'Boosting',
    'genshin-impact': 'Boosting',
    'league-of-legends': 'Boosting',
    'league-of-legends-wild-rift': 'Boosting',
    'marvel-rivals': 'Boosting',
    'mobile-legends': 'Boosting',
    'monster-hunter-wilds': 'Boosting',
    'new-world': 'Boosting',
    'old-school-runescape': 'Boosting',
    'overwatch-2': 'Boosting',
    'path-of-exile': 'Boosting',
    'path-of-exile-2': 'Boosting',
    'pokemon-go': 'Boosting',
    'rainbow-six-siege-x': 'Boosting',
    'roblox': 'Boosting',
    'rocket-league': 'Boosting',
    'runescape-3': 'Boosting',
    'rust': 'Boosting',
    'teamfight-tactics': 'Boosting',
    'the-first-descendant': 'Boosting',
    'the-forge': 'Boosting',
    'throne-and-liberty': 'Boosting',
    'valorant': 'Boosting',
    'warframe': 'Boosting',
    'where-winds-meet': 'Boosting',
    'world-of-warcraft': 'Boosting',
    'warcraft': 'Boosting',
    'wow-classic': 'Boosting',
    'wow-mists-of-pandaria': 'Boosting'
  };

  // Games with boosting pages
  const gamesWithBoostingSlugs = Object.keys(boostingNameMapForDisplay);

  // Boosting name mapping for display in popular games
  const getBoostingDisplayName = (gameName) => {
    const gameSlug = getGameSlug(gameName);
    const boostingName = boostingNameMapForDisplay[gameSlug];
    return boostingName ? `${gameName} ${boostingName}` : gameName;
  };

  const categories = [
    { name: 'Currency', key: 'nav.currency' },
    { name: 'Accounts', key: 'nav.accounts' },
    { name: 'Top Ups', key: 'nav.topUps' },
    { name: 'Items', key: 'nav.items' },
    { name: 'Boosting', key: 'nav.boosting' },
    { name: 'Gift Cards', key: 'nav.giftCards', to: '/amazon/top-ups' },
    { name: 'Drops', key: 'nav.drops', to: '/twitch-drops/items' },
    { name: 'Subscriptions', key: 'nav.subscriptions', to: '/netflix/accounts' },
  ];

  const mobileCategoryMeta = {
    'nav.currency': { Icon: Diamond, hint: 'Gold, coins & in-game cash' },
    'nav.accounts': { Icon: UserCircle, hint: 'Ready-to-play accounts' },
    'nav.topUps': { Icon: Wallet, hint: 'V-Bucks, RP & credits' },
    'nav.items': { Icon: CrossedSwords, hint: 'Skins, loot & gear' },
    'nav.boosting': { Icon: Lightning, hint: 'Ranks, wins & carries' },
    'nav.giftCards': { Icon: Gift, hint: 'Store & platform cards' },
    'nav.drops': { Icon: Drop, hint: 'Twitch drops & rewards' },
    'nav.subscriptions': { Icon: Star, hint: 'Netflix, Spotify & more' },
  };

  const [categoriesDropdownOpen, setCategoriesDropdownOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [shouldAnimateGameDropdown, setShouldAnimateGameDropdown] = useState(false);

  // Memoize filtered games to prevent recalculation on every render
  // Games with currency pages
  const gamesWithCurrencySlugs = [
    '8-ball-pool', 'albion-online', 'animal-crossing-new-horizons', 'arc-raiders', 'black-desert-online',
    'dark-and-darker', 'delta-force', 'diablo-4', 'digimon-super-rumble', 'donutsmp',
    'ea-sports-fc', 'elden-ring', 'elder-scrolls-online', 'escape-from-tarkov', 'fallout-76',
    'final-fantasy-xiv', 'fisch', 'forza-horizon-5', 'grow-a-garden', 'hypixel', 'lost-ark',
    'minecraft', 'new-world', 'old-school-runescape', 'path-of-exile', 'path-of-exile-2',
    'pet-simulator-99', 'pets-go', 'pokemon-go', 'roblox', 'runescape-3', 'runescape-bonds',
    'star-citizen', 'swtor', 'temtem', 'throne-and-liberty', 'toilet-tower-defense',
    'torchlight-infinite', 'trove', 'warframe', 'warcraft', 'wow-classic', 'wow-mists-of-pandaria',
    'blade-soul-neo', 'blade-ball', 'creatures-of-sonaria', 'dc-universe-online', 'dofus',
    'dune-awakening', 'ea-sports-fc-mobile', 'eve-echoes', 'eve-online', 'final-fantasy-xi',
    'growtopia', 'guild-wars-2', 'last-epoch', 'madden-nfl', 'maplestory', 'mortal-online-2',
    'odin-valhalla-rising'
  ];

  const filteredGames = useMemo(() => {
    let gamesToFilter = allGames;
    
    // If we're in the Currency category dropdown, only show games with currency pages
    // Note: This will be further filtered by category when rendering
    if (!searchQuery.trim()) {
      return gamesToFilter; // Show all games when search is empty (will be filtered by category in render)
    }
    const query = searchQuery.toLowerCase().trim();
    
    // Check if query matches any alias directly
    const aliasMatch = gameAliases[query];
    if (aliasMatch && gamesToFilter.includes(aliasMatch)) {
      return [aliasMatch];
    }
    
    return sortGamesBySearchRelevance(
      gamesToFilter.filter((game) => matchesGame(game, query, gameAliases)),
      query,
      gameAliases
    );
  }, [searchQuery]);

  // Get categories for a game (similar to SubNavbar logic)
  const getGameCategories = (gameSlug, gameName) => {
    const gamesWithCurrency = [
      '8-ball-pool', 'albion-online', 'animal-crossing-new-horizons', 'arc-raiders', 'black-desert-online',
      'dark-and-darker', 'delta-force', 'diablo-4', 'digimon-super-rumble', 'donutsmp',
      'ea-sports-fc', 'elden-ring', 'elder-scrolls-online', 'escape-from-tarkov', 'fallout-76',
      'final-fantasy-xiv', 'fisch', 'forza-horizon-5', 'grow-a-garden', 'hypixel', 'lost-ark',
      'minecraft', 'new-world', 'old-school-runescape', 'path-of-exile', 'path-of-exile-2',
      'pet-simulator-99', 'pets-go', 'pokemon-go', 'roblox', 'runescape-3', 'runescape-bonds',
      'star-citizen', 'swtor', 'temtem', 'throne-and-liberty', 'toilet-tower-defense',
      'torchlight-infinite', 'trove', 'warframe', 'warcraft', 'wow-classic', 'wow-mists-of-pandaria',
      'blade-soul-neo', 'blade-ball', 'creatures-of-sonaria', 'dc-universe-online', 'dofus',
      'dune-awakening', 'ea-sports-fc-mobile', 'eve-echoes', 'eve-online', 'final-fantasy-xi',
      'growtopia', 'guild-wars-2', 'last-epoch', 'madden-nfl', 'maplestory', 'mortal-online-2',
      'odin-valhalla-rising'
    ];

    // Currency name mapping for each game
    const currencyNameMap = {
      '8-ball-pool': 'Coins',
      'albion-online': 'Silver',
      'animal-crossing-new-horizons': 'Bells',
      'arc-raiders': 'Coins',
      'black-desert-online': 'Silver',
      'blade-soul-neo': 'Divine Gems',
      'blade-ball': 'Tokens',
      'creatures-of-sonaria': 'Mushrooms',
      'dark-and-darker': 'Gold',
      'dc-universe-online': 'Cash',
      'delta-force': 'Tekniq Alloy',
      'diablo-4': 'Gold',
      'digimon-super-rumble': 'Bits',
      'dofus': 'Kamas',
      'donutsmp': 'Money',
      'dune-awakening': 'Solaris',
      'ea-sports-fc': 'Coins',
      'ea-sports-fc-mobile': 'Coins',
      'elden-ring': 'Runes',
      'elder-scrolls-online': 'Gold',
      'escape-from-tarkov': 'Roubles',
      'eve-echoes': 'ISK',
      'eve-online': 'ISK',
      'fallout-76': 'Caps',
      'final-fantasy-xi': 'Gil',
      'final-fantasy-xiv': 'Gil',
      'fisch': 'Money',
      'forza-horizon-5': 'Credits',
      'grow-a-garden': 'Tokens',
      'growtopia': 'Locks',
      'guild-wars-2': 'Gold',
      'hypixel': 'Coins',
      'last-epoch': 'Gold',
      'lost-ark': 'Gold',
      'madden-nfl': 'Coins',
      'maplestory': 'Mesos',
      'minecraft': 'Hypixel Coins',
      'mortal-online-2': 'Gold',
      'new-world': 'Coins',
      'odin-valhalla-rising': 'Diamonds',
      'old-school-runescape': 'Gold',
      'path-of-exile': 'Currency',
      'path-of-exile-2': 'Currency',
      'pet-simulator-99': 'Gems',
      'pets-go': 'Diamonds',
      'pokemon-go': 'Stardust',
      'roblox': 'Robux',
      'runescape-3': 'Gold',
      'runescape-bonds': 'Bonds',
      'star-citizen': 'aUEC',
      'swtor': 'Credits',
      'temtem': 'Pansun',
      'throne-and-liberty': 'Lucent',
      'toilet-tower-defense': 'Gems',
      'torchlight-infinite': 'Flame Elementium',
      'trove': 'Flux',
      'warframe': 'Platinum',
      'warcraft': 'Gold',
      'wow-classic': 'Gold',
      'wow-mists-of-pandaria': 'Gold'
    };
    
    if (gameSlug === 'donutsmp') {
      return [
        { name: currencyNameMap[gameSlug] || 'Currency', path: `/${gameSlug}/currency` },
        { name: 'Accounts', path: `/${gameSlug}/accounts` },
        { name: 'Items', path: `/${gameSlug}/items` },
        { name: 'Boosting', path: `/${gameSlug}/boosting` },
      ];
    }
    if (gameSlug === 'fisch') {
      return [
        { name: currencyNameMap[gameSlug] || 'Currency', path: `/${gameSlug}/currency` },
        { name: 'Accounts', path: `/${gameSlug}/accounts` },
        { name: 'Items', path: `/${gameSlug}/items` },
        { name: 'Boosting', path: `/${gameSlug}/boosting` },
      ];
    }
    if (gameSlug === 'arena-breakout') {
      return [
        { name: 'Accounts', path: `/${gameSlug}/accounts` },
        { name: 'Top Ups', path: `/${gameSlug}/top-ups` },
        { name: 'Boosting', path: `/${gameSlug}/boosting` },
      ];
    }
    if (gameSlug === 'battlefield') {
      return [
        { name: 'Accounts', path: `/${gameSlug}/accounts` },
        { name: 'Items', path: `/${gameSlug}/items` },
        { name: 'Boosting', path: `/${gameSlug}/boosting` },
      ];
    }
    if (gamesWithCurrency.includes(gameSlug) && gameSlug !== 'arc-raiders') {
      const categories = [
        { name: currencyNameMap[gameSlug] || 'Currency', path: `/${gameSlug}/currency` },
        { name: 'Accounts', path: `/${gameSlug}/accounts` },
        { name: 'Top Ups', path: `/${gameSlug}/top-ups` },
        { name: 'Items', path: `/${gameSlug}/items` },
      ];
      if (
        gameSlug === 'escape-from-tarkov' ||
        gameSlug === 'black-desert-online' ||
        gameSlug === 'delta-force' ||
        gameSlug === 'diablo-4' ||
        gameSlug === 'new-world'
      ) {
        categories.push({ name: 'Boosting', path: `/${gameSlug}/boosting` });
      }
      return categories;
    }
    if (gameSlug === 'arc-raiders') {
      return [
        { name: currencyNameMap[gameSlug] || 'Currency', path: `/${gameSlug}/currency` },
        { name: 'Accounts', path: `/${gameSlug}/accounts` },
        { name: 'Items', path: `/${gameSlug}/items` },
        { name: 'Boosting', path: `/${gameSlug}/boosting` },
      ];
    }
    if (gameSlug === 'cs2' || gameSlug === 'counter-strike-2') {
      return [
        { name: 'Accounts', path: `/${gameSlug}/accounts` },
        { name: 'Items', path: `/${gameSlug}/items` },
        { name: 'Boosting', path: `/${gameSlug}/boosting` },
      ];
    }
    
    // Use the same category lists as SubNavbar to determine which categories exist
    const gamesWithTopUps = [
      '99-nights-in-the-forest', 'amazon', 'anime-vanguards', 'apex-legends', 'arena-breakout', 'arena-breakout-infinite', 'bigo-live',
      'blood-strike', 'brawl-stars', 'call-of-duty-mobile', 'cod-call-of-duty', 'call-of-duty',
      'clash-of-clans', 'clash-royale', 'counter-strike-2', 'crunchyroll', 'dead-by-daylight', 'delta-force',
      'destiny-2', 'ea-play', 'ea-sports-fc', 'efootball', 'elder-scrolls-online',
      'escape-from-tarkov', 'etheria-restart', 'fortnite', 'garena-free-fire', 'genshin-impact', 'grand-roleplay', 'growtopia',
      'honkai-star-rail', 'honor-of-kings', 'hypixel', 'identity-v',
      'league-of-legends', 'league-of-legends-wild-rift', 'love-and-deepspace', 'marvel-rivals',
      'miliastra-wonderland', 'minecraft', 'mobile-legends', 'nintendo',
      'overwatch-2', 'path-of-exile-2', 'playstation', 'pokemon-go', 'pokemon-tcg-pocket', 'poppo-live',
      'pubg', 'pubg-mobile', 'rainbow-six-siege', 'rainbow-six-siege-x', 'roblox', 'rocket-league', 'rust',
      'sea-of-thieves', 'spotify', 'super-sus', 'tiktok', 'valorant', 'whiteout-survival',
      'world-of-warcraft', 'warcraft', 'wuthering-waves', 'youtube', 'zenless-zone-zero'
    ];
    
    const gamesWithItems = [
      '8-ball-pool', 'adopt-me', 'albion-online', 'all-star-tower-defense-x', 'animal-crossing-new-horizons',
      'anime-defenders', 'anime-eternal', 'anime-fighting-simulator-endless', 'anime-last-stand',
      'anime-reborn', 'anime-vanguards', 'apex-legends', 'arc-raiders', 'ark-survival-ascended',
      'arknights', 'attack-on-titan-revolution', 'arena-breakout-infinite', 'ayakashi-rumble',
      'battlefield', 'battlenet', 'bee-swarm-simulator', 'black-clover-m', 'black-desert-online',
      'bleach-brave-souls', 'blade-ball', 'blood-strike', 'blox-fruits', 'blue-lock-rivals',
      'borderlands-4', 'brawl-stars', 'bubble-gum-simulator-infinity', 'build-a-zoo',
      'call-of-duty-mobile', 'car-parking-multiplayer', 'chaos-zero-nightmare', 'chocotto-land',
      'clash-royale', 'cod-call-of-duty', 'call-of-duty', 'clash-of-clans', 'cookie-run-kingdom',
      'counter-strike-2', 'crunchyroll', 'cs2', 'creatures-of-sonaria', 'dark-and-darker',
      'darkorbit', 'dayz', 'dead-by-daylight', 'dead-rails', 'delta-force', 'destiny-2',
      'diablo-2-resurrected', 'diablo-4', 'diablo-immortal', 'digimon-super-rumble', 'discord',
      'donutsmp', 'dota-2', 'dragon-ball-legends', 'dragon-ball-z-dokkan-battle',
      'dune-awakening', 'duet-night-abyss', 'ea-sports-fc', 'efootball', 'elden-ring',
      'elder-scrolls-online', 'epic-games', 'epic-seven', 'escape-from-tarkov', 'etheria-restart',
      'eve-online', 'fallout-76', 'fate-grand-order', 'final-fantasy-xiv', 'fisch', 'fish-it',
      'forza-horizon-5', 'fortnite', 'garden-tower-defense', 'garena-free-fire', 'genshin-impact',
      'girls-frontline-2-exilium', 'goddess-of-victory-nikke', 'golden-hoyeah', 'granblue-fantasy',
      'grand-piece-online', 'grand-roleplay', 'grand-theft-auto-5', 'grow-a-garden', 'growtopia',
      'guild-wars-2', 'hay-day', 'honkai-impact-3rd', 'honkai-star-rail', 'houchi-shoujo',
      'hunty-zombie', 'hypixel', 'identity-v', 'ink-game', 'jailbreak', 'jojo-s-bizarre-adventure-ora-ora-overdrive',
      'jujutsu-infinite', 'jujutsu-kaisen-phantom-parade', 'kaiju-no-8', 'king-legacy', 'knight-online',
      'knives-out', 'last-island-of-survival', 'league-of-legends',
      'livly-island', 'lords-mobile', 'lost-ark', 'madoka-magica-magia-exedra',
      'marvel-contest-of-champions', 'marvel-rivals', 'metin2', 'millennium-war-aigis', 'minecraft',
      'mobile-legends', 'monopoly-go', 'monster-hunter-now', 'monster-strike', 'murder-mystery-2',
      'murderers-vs-sheriffs-duels', 'netflix', 'new-world', 'old-school-runescape', 'one-piece-bounty-rush',
      'one-piece-treasure-cruise', 'origin', 'overwatch-2', 'path-of-exile', 'path-of-exile-2',
      'persona-5-the-phantom-x', 'pet-simulator-99', 'pets-go', 'plants-vs-brainrots',
      'pokecolo', 'pokecolo-twin', 'pokemon-go', 'pokemon-legends-z-a', 'pokemon-tcg-pocket',
      'princess-connect-re-dive', 'professional-baseball-spirits-a', 'project-sekai', 'pubg',
      'pubg-mobile', 'puzzle-and-dragons', 'raise-animals', 'raid-shadow-legends', 'rainbow-six-siege',
      'rise-of-kingdoms', 'roblox', 'roblox-limiteds', 'roblox-rivals',
      'royale-online', 'runescape-3', 'rust', 'sd-gundam-g-generation-eternal',
      'seven-knights-re-birth', 'shadowverse', 'silver-and-blood', 'sky-children-of-the-light',
      'standoff-2', 'steal-a-brainrot', 'stella-sora', 'summoners-war',
      'team-fortress-2', 'the-finals', 'the-forge', 'the-seven-deadly-sins', 'throne-and-liberty',
      'tips-jar', 'tower-of-fantasy', 'twitch-drops', 'ubisoft-connect', 'uma-musume-pretty-derby',
      'universal-tower-defense', 'unveil-the-world', 'valorant', 'volleyball-legends', 'war-thunder',
      'wartune-ultra', 'watcher-of-realms', 'warframe', 'where-winds-meet', 'white-cat-project',
      'wolfteam', 'world-of-tanks', 'world-of-warships', 'world-of-warcraft',
      'warcraft', 'wow-classic', 'wow-mists-of-pandaria', 'wuthering-waves',
      'yo-kai-watch-wibble-wobble', 'yu-gi-oh-master-duel', 'zula'
    ];
    
    const gamesWithBoosting = [
      'anime-vanguards', 'apex-legends', 'arc-raiders', 'arena-breakout', 'arena-breakout-infinite',
      'battlefield', 'black-desert-online', 'blox-fruits', 'brawl-stars', 'call-of-duty-mobile',
      'cod-call-of-duty', 'call-of-duty', 'clash-of-clans', 'clash-royale', 'counter-strike-2', 'cs2',
      'dead-by-daylight', 'deadlock', 'delta-force', 'destiny-2', 'diablo-4',
      'donutsmp', 'dota-2', 'ea-sports-fc', 'elder-scrolls-online', 'escape-from-tarkov',
      'final-fantasy-xiv', 'fisch', 'fortnite', 'genshin-impact', 'grand-roleplay', 'league-of-legends',
      'league-of-legends-wild-rift', 'marvel-rivals', 'minecraft', 'mobile-legends', 'monster-hunter-wilds',
      'new-world', 'old-school-runescape', 'overwatch-2', 'path-of-exile', 'path-of-exile-2',
      'pokemon-go', 'rainbow-six-siege', 'rainbow-six-siege-x', 'roblox', 'rocket-league', 'runescape-3', 'rust',
      'teamfight-tactics', 'the-first-descendant', 'the-forge', 'throne-and-liberty', 'valorant',
      'warframe', 'where-winds-meet', 'world-of-warcraft', 'warcraft', 'wow-classic',
      'wow-mists-of-pandaria'
    ];
    
    // Build categories dynamically based on what the game actually has
    const categories = [];
    
    // Add Currency if game has currency
    if (gamesWithCurrency.includes(gameSlug)) {
      categories.push({
        name: currencyNameMap[gameSlug] || 'Currency',
        path: `/${gameSlug}/currency`
      });
    }
    
    // All games have Accounts
    categories.push({
      name: 'Accounts',
      path: `/${gameSlug}/accounts`
    });
    
    // Add Top Ups if game has top-ups
    if (gamesWithTopUps.includes(gameSlug)) {
      categories.push({
        name: 'Top Ups',
        path: `/${gameSlug}/top-ups`
      });
    }
    
    // Add Items if game has items
    if (gamesWithItems.includes(gameSlug)) {
      categories.push({
        name: 'Items',
        path: `/${gameSlug}/items`
      });
    }
    
    // Add Boosting if game has boosting
    if (gamesWithBoosting.includes(gameSlug)) {
      categories.push({
        name: 'Boosting',
        path: `/${gameSlug}/boosting`
      });
    }
    
    return categories;
  };

  // Save search to recent searches
  const saveRecentSearch = useCallback((game, category, path) => {
    const searchItem = { game, category, path, timestamp: Date.now() };
    setRecentSearches(prev => {
      // Remove duplicates and add new one at the beginning
      const filtered = prev.filter(item => 
        !(item.game === game && item.category === category)
      );
      const updated = [searchItem, ...filtered].slice(0, 10); // Keep only last 10
      localStorage.setItem('recentSearches', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Clear recent searches
  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  }, []);

  // Get popular categories for search dropdown
  const popularCategories = useMemo(() => {
    const results = [];
    NAVBAR_SEARCH_POPULAR_ORDER.forEach((game) => {
      const gameSlug = getGameSlug(game);
      const categories = getGameCategories(gameSlug, game);
      // Take first category for each popular game
      if (categories.length > 0) {
        results.push({
          game,
          gameSlug,
          category: categories[0].name,
          path: categories[0].path,
          iconPath: getGameIcon(game)
        });
      }
    });
    return results;
  }, []);

  // Navbar search: all subcategories, mixed (round-robin across games) + popular category order
  const navbarSearchResults = useMemo(() => {
    if (!navbarSearchQuery.trim()) return [];
    const q = navbarSearchQuery.toLowerCase().trim();
    const games = rankedSearchGames(allGames, q, gameAliases, { limit: 8 });
    const MAX_ROWS = 48;

    const perGame = games.map((game) => {
      const gameSlug = getGameSlug(game);
      let categories = getGameCategories(gameSlug, game);
      const iconPath = getGameIcon(game);
      if (!categories.length) {
        return [
          {
            key: `${gameSlug}-home`,
            game,
            category: 'Home',
            path: `/${gameSlug}`,
            iconPath,
          },
        ];
      }
      categories = sortNavbarCategoriesForSearch(categories, q);
      return categories.map((cat) => ({
        key: `${gameSlug}-${cat.path}`,
        game,
        category: cat.name,
        path: cat.path,
        iconPath,
      }));
    });

    return interleaveNavbarCategoryRows(perGame).slice(0, MAX_ROWS);
  }, [navbarSearchQuery]);

  // Memoize handlers to prevent unnecessary re-renders
  const handleMouseEnter = useCallback((index) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setHoveredCategory(index);
    // Only prevent scroll on desktop
    if (window.innerWidth > 768) {
      document.body.style.overflow = 'hidden';
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setHoveredCategory(null);
      document.body.style.overflow = '';
      timeoutRef.current = null;
    }, 150);
  }, []);

  const handleDropdownMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Only prevent scroll on desktop
    if (window.innerWidth > 768) {
      document.body.style.overflow = 'hidden';
    }
  }, []);

  const handleDropdownMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setHoveredCategory(null);
      document.body.style.overflow = '';
      timeoutRef.current = null;
    }, 150);
  }, []);

  const categoryGameItems = useMemo(() => {
    if (hoveredCategory == null) return [];
    const categoryName = categories[hoveredCategory]?.name || '';
    const key = categoryName.toLowerCase();
    let list = allGames.filter((game) => {
      const slug = getGameSlug(game);
      if (key === 'currency') return gamesWithCurrencySlugs.includes(slug);
      if (key === 'top ups') return gamesWithTopUpsSlugs.includes(slug);
      if (key === 'items') return gamesWithItemsSlugs.includes(slug);
      if (key === 'boosting') return gamesWithBoostingSlugs.includes(slug);
      return true;
    });
    const query = searchQuery.trim().toLowerCase();
    if (query) {
      list = sortGamesBySearchRelevance(
        list.filter((game) => matchesGame(game, query, gameAliases)),
        query,
        gameAliases
      );
    } else {
      list = [...list].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }
    return list.map((game) => {
      const slug = getGameSlug(game);
      let href = `/${slug}`;
      if (key === 'currency' && gamesWithCurrencySlugs.includes(slug)) {
        href = `/${slug}/currency`;
      } else if (key === 'accounts' || key === 'boosting' || key === 'top ups' || key === 'items') {
        href = `/${slug}/${key === 'top ups' ? 'top-ups' : key}`;
      }
      return {
        game,
        href,
        iconPath:
          key === 'currency' && slug === 'hypixel'
            ? '/main/games/icons/hypixel.jpg'
            : getGameIcon(game),
      };
    });
  }, [hoveredCategory, searchQuery]);

  useEffect(() => {
    setSearchQuery('');
  }, [hoveredCategory]);

  const closeCategoryDropdown = () => {
    setHoveredCategory(null);
    setShouldAnimateGameDropdown(false);
    setMobileCategoriesOpen(false);
    document.body.style.overflow = '';
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    document.body.style.overflow = '';
  };

  const toggleMobileCategories = () => {
    closeMobileSearch();
    setShowSettingsDropdown(false);
    setMobileCategoriesOpen((open) => {
      if (open) {
        document.body.style.overflow = '';
        setShouldAnimateGameDropdown(false);
        return false;
      }
      document.body.style.overflow = 'hidden';
      return true;
    });
  };

  return (
    <header className={`header${mobileSearchOpen ? ' header--search-open' : ''}${isHome ? ' header--home' : ''}${isProfile ? ' header--profile' : ''}`}>
      <div className="header-container">
        {!isProfile ? (
        <div className="mobile-categories-wrapper">
          <button
            className="mobile-categories-btn"
            onClick={toggleMobileCategories}
          >
              <MenuIcon color="currentColor" size={20} />
            </button>
          <div className={`mobile-categories-dropdown ${mobileCategoriesOpen ? 'open' : ''}`}>
            <div className="mobile-categories-header">
              <div style={{ width: '40px' }}></div>
              <h3>Browse</h3>
              <button 
                className="mobile-categories-close"
                onClick={() => {
                  setMobileCategoriesOpen(false);
                  document.body.style.overflow = '';
                }}
              >
                Done
              </button>
            </div>
            <div className="mobile-categories-list">
              {categories.map((category, index) => {
                const meta = mobileCategoryMeta[category.key] || {};
                const CategoryIcon = meta.Icon;
                const label = (
                  <span className="mobile-category-lead">
                    {CategoryIcon ? (
                      <span className="mobile-category-icon" aria-hidden>
                        <CategoryIcon size={18} weight="light" />
                      </span>
                    ) : null}
                    <span className="mobile-category-copy">
                      <span className="mobile-category-name">
                        {category.key ? t(category.key) : category.name}
                      </span>
                      {meta.hint ? (
                        <span className="mobile-category-hint">{meta.hint}</span>
                      ) : null}
                    </span>
                  </span>
                );
                return category.to ? (
                  <Link
                    key={index}
                    to={category.to}
                    className="mobile-category-link"
                    onClick={() => {
                      setMobileCategoriesOpen(false);
                      setHoveredCategory(null);
                      setShouldAnimateGameDropdown(false);
                      document.body.style.overflow = '';
                    }}
                  >
                    {label}
                    <ChevronRight size={18} color="currentColor" />
                  </Link>
                ) : (
                  <a
                    key={index}
                    href={`#${category.name.toLowerCase()}`}
                    className="mobile-category-link"
                    onClick={(e) => {
                      e.preventDefault();
                      setShouldAnimateGameDropdown(true);
                      setHoveredCategory(index);
                      document.body.style.overflow = '';
                    }}
                  >
                    {label}
                    <ChevronRight size={18} color="currentColor" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
        ) : null}
        <div className={`header-brand${isHome ? ' header-brand--home' : ''}`}>
          <Link to="/" className={`logo${isHome ? ' logo--home' : ''}`} aria-label="XMARKET">
            {isHome ? (
              <>
                <XmarketLogo className="logo-img logo-desktop" />
                <XmarketLogo compact className="logo-img logo-mobile" />
              </>
            ) : (
              <>
                <img src="/main/logo/XMARK 1.svg" alt="XMARKET" className="logo-img logo-desktop" />
                <img src="/main/logo/xmarkmobile.svg" alt="XMARKET" className="logo-img logo-mobile" />
              </>
            )}
          </Link>
          {isHome && isDesktop ? (
            <button
              type="button"
              className="header-theme-switch"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? <MoonThin size={18} weight="regular" /> : <SunThin size={18} weight="regular" />}
            </button>
          ) : null}
        </div>
        {!isProfile ? (
        <nav className={`nav-menu nav-menu-left ${isHome ? 'nav-menu--home' : ''} ${mobileMenuOpen ? 'mobile-open' : ''} ${showNavbarSearchResults && isDesktop ? 'search-open' : ''}`}>
          {categories.map((category, index) => {
            const chipLabel = category.key ? t(category.key) : category.name;
            return category.to ? (
              <div key={index} className="nav-item">
                <Link
                  to={category.to}
                  className={isHome && isDesktop ? 'header-cat-chip' : 'nav-link'}
                  title=""
                  onMouseEnter={() => {
                    if (timeoutRef.current) {
                      clearTimeout(timeoutRef.current);
                      timeoutRef.current = null;
                    }
                    setHoveredCategory(null);
                    document.body.style.overflow = '';
                  }}
                  onClick={() => {
                    setHoveredCategory(null);
                    if (window.innerWidth <= 768) {
                      closeMobileMenu();
                    }
                  }}
                >
                  {chipLabel}
                </Link>
              </div>
            ) : (
            <div
              key={index}
              className={`nav-item${hoveredCategory === index ? ' is-open' : ''}`}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
            >
              <a 
                href={`#${category.name.toLowerCase()}`} 
                className={isHome && isDesktop ? 'header-cat-chip' : 'nav-link'}
                title=""
                onClick={(e) => {
                  e.preventDefault();
                  if (window.innerWidth <= 768) {
                    setHoveredCategory(hoveredCategory === index ? null : index);
                  }
                }}
              >
                {chipLabel}
              </a>
              {hoveredCategory === index ? (
              <div
                className="dropdown-menu dropdown-open"
                onMouseEnter={handleDropdownMouseEnter}
                onMouseLeave={handleDropdownMouseLeave}
              >
                <CategoryGamesPanel
                  title={chipLabel}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  items={categoryGameItems}
                  onNavigate={() => {
                    setHoveredCategory(null);
                    if (window.innerWidth <= 768) {
                      closeMobileMenu();
                    }
                  }}
                />
              </div>
              ) : null}
            </div>
            );
          })}
        </nav>
        ) : null}
        <>
          {/* Overlay for desktop search - darkens the page - rendered via portal to cover entire screen */}
          {showNavbarSearchResults && isDesktop && typeof document !== 'undefined' && createPortal(
            <div 
              className="search-overlay"
              onClick={(e) => {
                // Only close if clicking directly on overlay, not on search area
                // Also check if user is actively interacting with search input
                if (e.target === e.currentTarget && !isSearchInteractingRef.current) {
                  setShowNavbarSearchResults(false);
                  // Don't clear the search query - let user keep it for next time
                }
              }}
              onMouseDown={(e) => {
                // Prevent closing if user is clicking within search area
                const searchWrapper = document.querySelector('.header-search-wrapper');
                if (searchWrapper && searchWrapper.contains(e.target)) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            />,
            document.body
          )}
          <div className="header-search-wrapper">
            <div 
              className="header-search"
              onClick={() => {
                if (!isDesktop) {
                  setMobileSearchOpen(true);
                }
              }}
            >
              {isHome ? <MagnifyingGlass size={18} weight="regular" /> : <SearchIcon size={18} />}
              <input 
                ref={searchInputRef}
                type="text" 
                placeholder={isHome ? 'Searching for games and apps...' : 'Search games'} 
                className="header-search-input"
                value={navbarSearchQuery}
                onMouseDown={() => {
                  // Track that user is interacting with search
                  isSearchInteractingRef.current = true;
                }}
                onChange={(e) => {
                  setNavbarSearchQuery(e.target.value);
                  isSearchInteractingRef.current = true;
                  // Always keep dropdown open when user is typing or clearing
                  // Keep it open even when input is empty, as long as user is interacting
                  if (isDesktop) {
                    setShowNavbarSearchResults(true);
                  }
                  // Preload icons when user starts typing
                  preloadAllIcons();
                }}
                onFocus={() => {
                  isSearchInteractingRef.current = true;
                  if (isDesktop) {
                    setShowNavbarSearchResults(true);
                  } else {
                    setMobileSearchOpen(true);
                  }
                  // Preload all icons immediately when search is focused
                  preloadAllIcons();
                }}
                onBlur={(e) => {
                  // Don't close dropdown on blur - keep it open when clearing input
                  // Only the overlay click will close it when user clicks outside
                  // This allows the search bar to stay open even when input is cleared
                  // Reset interaction flag after a short delay
                  setTimeout(() => {
                    isSearchInteractingRef.current = false;
                  }, 100);
                }}
              />
            </div>
            {showNavbarSearchResults && (
              <div className="navbar-search-results">
                {navbarSearchQuery.trim() ? (
                  navbarSearchResults.length > 0 ? (
                    <>
                      {navbarSearchResults.map((row) => (
                        <Link
                          key={row.key}
                          to={row.path}
                          className="navbar-search-result-item"
                          onClick={() => {
                            saveRecentSearch(row.game, row.category, row.path);
                            setNavbarSearchQuery('');
                            setShowNavbarSearchResults(false);
                          }}
                        >
                          {row.iconPath ? (
                            <img
                              src={row.iconPath}
                              alt=""
                              className="navbar-search-game-icon"
                              loading="eager"
                              fetchPriority="high"
                              decoding="async"
                            />
                          ) : (
                            <div className="navbar-search-game-icon" />
                          )}
                          <span className="navbar-search-game-name">
                            {row.game} {row.category}
                          </span>
                        </Link>
                      ))}
                    </>
                  ) : (
                    <div className="navbar-search-empty">
                      <span>No results found</span>
                    </div>
                  )
                ) : (
                  // Show recently searched and popular categories when empty
                  <>
                    {recentSearches.length > 0 && (
                      <div className="navbar-search-section">
                        <div className="navbar-search-section-header">
                          <span className="navbar-search-section-title">RECENTLY SEARCHED</span>
                          <button 
                            className="navbar-search-clear-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              clearRecentSearches();
                            }}
                          >
                            Clear all
                          </button>
                        </div>
                        <div className="navbar-search-tags">
                          {recentSearches.map((item, index) => (
                            <div key={index} className="navbar-search-tag">
                              <Link
                                to={item.path}
                                className="navbar-search-tag-link"
                                onClick={() => {
                                  setShowNavbarSearchResults(false);
                                }}
                              >
                                {item.game} {item.category}
                              </Link>
                              <button
                                className="navbar-search-tag-remove"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRecentSearches(prev => {
                                    const updated = prev.filter((_, i) => i !== index);
                                    localStorage.setItem('recentSearches', JSON.stringify(updated));
                                    return updated;
                                  });
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="navbar-search-section">
                      <div className="navbar-search-section-header">
                        <span className="navbar-search-section-title">POPULAR CATEGORIES</span>
                      </div>
                      <div className="navbar-search-popular">
                        {popularCategories.map((item, index) => (
                          <Link
                            key={index}
                            to={item.path}
                            className="navbar-search-popular-item"
                            onClick={() => {
                              saveRecentSearch(item.game, item.category, item.path);
                              setShowNavbarSearchResults(false);
                            }}
                          >
                            {item.iconPath ? (
                              <img 
                                src={item.iconPath} 
                                alt={item.game} 
                                className="navbar-search-popular-icon"
                                loading="eager"
                                fetchPriority="high"
                                decoding="async"
                              />
                            ) : (
                              <div className="navbar-search-popular-icon"></div>
                            )}
                            <span className="navbar-search-popular-name">{item.game} {item.category}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </>
        {/* Results sit under the header so the search field stays on screen. */}
        {mobileSearchOpen && !isDesktop && createPortal(
          <div className="mobile-search-sheet" role="listbox" aria-label="Search results">
            <div className="mobile-search-content">
              {navbarSearchQuery.trim() ? (
                navbarSearchResults.length > 0 ? (
                  <div className="mobile-search-results-list">
                    {navbarSearchResults.map((row) => (
                      <Link
                        key={row.key}
                        to={row.path}
                        className="mobile-search-result-item"
                        onClick={() => {
                          saveRecentSearch(row.game, row.category, row.path);
                          closeMobileSearch();
                        }}
                      >
                        {row.iconPath ? (
                          <img
                            src={row.iconPath}
                            alt=""
                            className="mobile-search-game-icon"
                            loading="eager"
                            fetchPriority="high"
                            decoding="async"
                          />
                        ) : (
                          <div className="mobile-search-game-icon" />
                        )}
                        <span className="mobile-search-game-name">
                          {row.game} {row.category}
                        </span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="mobile-search-empty">
                    <span>No results found</span>
                  </div>
                )
              ) : (
                // Show recently searched and popular categories when empty
                <>
                  {recentSearches.length > 0 && (
                    <div className="mobile-search-section">
                      <div className="mobile-search-section-header">
                        <span className="mobile-search-section-title">RECENTLY SEARCHED</span>
                        <button 
                          className="mobile-search-clear-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearRecentSearches();
                          }}
                        >
                          Clear all
                        </button>
                      </div>
                      <div className="mobile-search-tags">
                        {recentSearches.map((item, index) => (
                          <div key={index} className="mobile-search-tag">
                            <Link
                              to={item.path}
                              className="mobile-search-tag-link"
                              onClick={() => {
                              closeMobileSearch();
                              }}
                            >
                              {item.game} {item.category}
                            </Link>
                            <button
                              className="mobile-search-tag-remove"
                              onClick={(e) => {
                                e.stopPropagation();
                                setRecentSearches(prev => {
                                  const updated = prev.filter((_, i) => i !== index);
                                  localStorage.setItem('recentSearches', JSON.stringify(updated));
                                  return updated;
                                });
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mobile-search-section">
                    <div className="mobile-search-section-header">
                      <span className="mobile-search-section-title">POPULAR CATEGORIES</span>
                    </div>
                    <div className="mobile-search-popular">
                      {popularCategories.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          className="mobile-search-popular-item"
                          onClick={() => {
                            saveRecentSearch(item.game, item.category, item.path);
                            closeMobileSearch();
                          }}
                        >
                          {item.iconPath ? (
                            <img 
                              src={item.iconPath} 
                              alt={item.game} 
                              className="mobile-search-popular-icon"
                              loading="eager"
                              fetchPriority="high"
                              decoding="async"
                            />
                          ) : (
                            <div className="mobile-search-popular-icon"></div>
                          )}
                          <span className="mobile-search-popular-name">{item.game} {item.category}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body
        )}
        <div className={`header-actions ${showNavbarSearchResults && isDesktop ? 'search-open' : ''}${isHome && isDesktop ? ' header-actions--home' : ''}`}>
          {isHome && isDesktop ? (
            <HomeHeaderTools userLoggedIn={userLoggedIn} location={location} />
          ) : (
            <>
          {(!isDesktop || (userLoggedIn && cartCount > 0)) && (
            <button
              type="button"
              className="header-cart-btn"
              title="Cart"
              onClick={() => setCartDrawerOpen(true)}
              aria-label="Open cart"
            >
              <CartIcon size={22} color="currentColor" />
              {cartCount > 0 ? <span className="cart-badge">{cartCount}</span> : null}
            </button>
          )}
          {userLoggedIn ? (
            <UserMenu />
          ) : (
            <Link to="/auth?signin=true" state={{ backgroundLocation: location }} className="btn-signin">
              Sign in
            </Link>
          )}
          <div className="settings-wrapper" ref={settingsDropdownRef}>
            <button
              className="settings-btn"
              onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
              title="Settings"
            >
              <Settings size={22} color="currentColor" />
            </button>
            {showSettingsDropdown && (
              <div className="settings-dropdown">
                <button
                  className="settings-dropdown-item language-currency-item"
                  onClick={() => {
                    setShowLanguageCurrencyModal(true);
                    setShowSettingsDropdown(false);
                  }}
                >
                  <Globe size={16} color="#ff6b35" />
                  <span>{currentLanguageData.nativeName} | {currentCurrency.code} - {currentCurrency.symbol}</span>
                  <ChevronRight size={16} color="#ff6b35" />
                </button>
                <button
                  className="settings-dropdown-item"
                  onClick={() => {
                    toggleTheme();
                    setShowSettingsDropdown(false);
                  }}
                >
                  {theme === 'dark' ? (
                    <Moon size={16} color="var(--text-primary)" />
                  ) : (
                    <Sun size={16} color="#ff6b35" />
                  )}
                  <span>Dark theme</span>
                  <div className={`theme-toggle ${theme === 'dark' ? 'active' : ''}`}>
                    <div className="theme-toggle-handle"></div>
                  </div>
                </button>
              </div>
            )}
          </div>
            </>
          )}
        </div>
        <LanguageCurrencyModal
          isOpen={showLanguageCurrencyModal}
          onClose={() => setShowLanguageCurrencyModal(false)}
          currentLanguage={currentLanguage}
          currentCurrency={currency}
          onLanguageChange={handleLanguageChange}
          onCurrencyChange={handleCurrencyChange}
          supportedLanguages={supportedLanguages}
        />
        {/* Cart drawer - opens automatically when item added */}
        {createPortal(
          <div
            className={`cart-drawer-overlay ${isCartDrawerOpen ? 'cart-drawer-open' : ''}`}
            onClick={() => setCartDrawerOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setCartDrawerOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Cart"
          >
            <div
              className="cart-drawer-panel"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="cart-drawer-header">
                <h2 className="cart-drawer-title">Cart</h2>
                <button
                  type="button"
                  className="cart-drawer-close"
                  onClick={() => setCartDrawerOpen(false)}
                  aria-label="Close cart"
                >
                  <CloseIcon size={24} />
                </button>
              </div>
              <div className="cart-drawer-body">
                {!cartItems || cartItems.length === 0 ? (
                  <p className="cart-drawer-empty">Your cart is empty</p>
                ) : (
                  <>
                    <ul className="cart-drawer-list">
                      {cartItems.map((item) => {
                        const ad = item.ad || {};
                        const isCurrency = ad.service_type === 'currency';
                        const qty = isCurrency && item.quantity != null ? Number(item.quantity) : 1;
                        const unitLabel = isCurrency ? (ad.metadata?.unit_label || (/8\s*ball\s*pool|8-ball-pool/i.test(ad.game || '') ? 'B' : /albion\s*online|animal\s*crossing|animal-crossing-new-horizons/i.test(ad.game || '') ? 'M' : /arc\s*raiders/i.test(ad.game || '') ? 'K' : '') || 'B') : '';
                        const unitPrice = getEffectivePrice(ad);
                        const lineTotal = unitPrice * qty;
                        return (
                          <li key={item.id} className="cart-drawer-item">
                            <div className="cart-drawer-item-info">
                              <span className="cart-drawer-item-game">{ad.game || 'Item'}</span>
                              <span className="cart-drawer-item-title">{ad.title || ad.service_type}</span>
                              {isCurrency && unitLabel ? (
                                <span className="cart-drawer-item-qty">{qty} {unitLabel}</span>
                              ) : qty > 1 ? (
                                <span className="cart-drawer-item-qty">× {qty}</span>
                              ) : null}
                            </div>
                            <div className="cart-drawer-item-right">
                              <span className="cart-drawer-item-price">€{lineTotal.toFixed(2)}</span>
                              <button
                                type="button"
                                className="cart-drawer-item-remove"
                                onClick={() => removeFromCart(item.id)}
                                aria-label="Remove from cart"
                              >
                                ×
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    <div className="cart-drawer-footer">
                      <div className="cart-drawer-total">
                        <span>Total</span>
                        <span>€{getCartTotal().toFixed(2)}</span>
                      </div>
                      <Link
                        to="/cart"
                        className="cart-drawer-view-cart"
                        onClick={() => setCartDrawerOpen(false)}
                      >
                        View cart
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
        {/* Full screen game dropdown for mobile */}
        <div 
          className={`mobile-game-dropdown-overlay ${hoveredCategory !== null ? 'visible' : 'hidden'} ${shouldAnimateGameDropdown ? 'animate-in' : ''}`}
          onClick={(e) => {
            // Only close if clicking directly on overlay, not on links
            if (e.target === e.currentTarget) {
              setShouldAnimateGameDropdown(false);
              setHoveredCategory(null);
            }
          }}
        >
            <div 
              className="mobile-game-dropdown"
              onClick={(e) => {
                // Don't close if clicking on a link
                if (e.target.closest('a')) {
                  return;
                }
                e.stopPropagation();
              }}
            >
              <div className="mobile-game-dropdown-header">
                <button 
                  className="mobile-game-dropdown-back"
                  onClick={() => {
                    setShouldAnimateGameDropdown(false);
                    setHoveredCategory(null);
                    // Categories dropdown is already visible underneath
                  }}
                >
                  <ChevronLeftIcon size={24} />
                </button>
                <h3>{categories[hoveredCategory]?.name}</h3>
                <button 
                  className="mobile-game-dropdown-close"
                  onClick={() => {
                    setHoveredCategory(null);
                    setMobileCategoriesOpen(false);
                    document.body.style.overflow = '';
                  }}
                >
                  Done
                </button>
              </div>
              <CategoryGamesPanel
                hideTitle
                title={categories[hoveredCategory]?.name}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                items={categoryGameItems}
                onNavigate={closeCategoryDropdown}
              />
            </div>
        </div>
      </div>
      
      {/* Full-width search sits under the top row on phone. Typing here opens results
          below the header instead of jumping into a second full-screen input. */}
      <div className={`mobile-search-row${mobileSearchOpen ? ' is-open' : ''}`}>
        <div className="mobile-search-bar">
          <SearchIcon size={16} color="currentColor" />
          <input
            ref={mobileSearchInputRef}
            type="text"
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder="Search games"
            className="mobile-search-input"
            value={navbarSearchQuery}
            onChange={(e) => {
              setNavbarSearchQuery(e.target.value);
              setMobileSearchOpen(true);
              preloadAllIcons();
            }}
            onFocus={() => {
              setMobileSearchOpen(true);
              setMobileCategoriesOpen(false);
              setShowSettingsDropdown(false);
              preloadAllIcons();
            }}
          />
          {navbarSearchQuery ? (
            <button
              type="button"
              className="mobile-search-clear-field"
              onClick={(e) => {
                e.preventDefault();
                setNavbarSearchQuery('');
                mobileSearchInputRef.current?.focus();
              }}
              aria-label="Clear search"
            >
              <CloseIcon size={14} color="currentColor" />
            </button>
          ) : null}
        </div>
        <button
          type="button"
          className="mobile-search-cancel"
          onClick={closeMobileSearch}
          tabIndex={mobileSearchOpen ? 0 : -1}
          aria-hidden={!mobileSearchOpen}
        >
          Cancel
        </button>
      </div>
      {!isDesktop
        ? createPortal(
            <MobileTabBar
              browseOpen={mobileCategoriesOpen}
              onBrowse={toggleMobileCategories}
              hidden={mobileSearchOpen || isProfile}
            />,
            document.body
          )
        : null}
    </header>
  );
};

export default Header;

